import { formatNumber } from "@/lib/format";
import { BreakdownRow } from "../types/webstoreAnalytics";

interface BreakdownListProps {
  title: string;
  subtitle?: string;
  rows: BreakdownRow[];
  limit?: number;
  /** Tailwind background class for the bar, e.g. "bg-accent-teal". */
  barClassName?: string;
  /** Turn a raw key into something a human reads — country codes, mostly. */
  formatKey?: (key: string) => string;
  emptyLabel?: string;
}

/**
 * One session dimension as a labelled bar list. Shared by device, browser, OS,
 * country and hostname so those five panels cannot drift apart visually.
 */
export function BreakdownList({
  title,
  subtitle,
  rows,
  limit = 6,
  barClassName = "bg-accent-blue",
  formatKey,
  emptyLabel = "No data in this range",
}: BreakdownListProps) {
  const shown = rows.slice(0, limit);

  return (
    <div className="panel p-5">
      <div className="mb-4">
        <div className="text-sm font-semibold text-ink-900">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>

      {shown.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center">{emptyLabel}</div>
      ) : (
        <div className="space-y-3.5">
          {shown.map((row) => (
            <div key={row.key} className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="font-medium text-slate-700 truncate" title={row.key}>
                  {formatKey ? formatKey(row.key) : row.key}
                </span>
                <span className="shrink-0 text-slate-600 numeric-value">
                  {formatNumber(row.sessions)}
                  <span className="text-slate-400 ml-1.5">{row.share_pct}%</span>
                </span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full ${barClassName}`}
                  style={{ width: `${row.share_pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length > limit && (
        <div className="text-[10px] text-slate-400 mt-3 text-right">
          Showing top {limit} of {rows.length}.
        </div>
      )}
    </div>
  );
}
